import type React from 'react';

import { AttentionTriangleIcon, DetectionIcon } from '@vlabs/icons';
import { FaceSample, Tag, Control } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import st from './LowFaceSimilarityConfirmation.module.sass';

const convertSimilarity = (similarity: number) => {
  return `${Math.ceil(similarity * 100).toString()} %`;
};

export type Context = 'changeFacePhoto' | 'addFaceToCard';

interface LowFaceSimilarityConfirmationProps {
  similarity: number;
  faceImageA?: string;
  faceImageB?: string;
  onConfirm: VoidFunction;
  onCancel: VoidFunction;
  context: Context;
}

function LowFaceSimilarityConfirmation({
  similarity,
  faceImageA = undefined,
  faceImageB = undefined,
  onConfirm,
  onCancel,
  context,
}: LowFaceSimilarityConfirmationProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={st.LowSimilarityConfirmation}>
      <div className={st.Text}>
        <div className={st.Title}>
          <AttentionTriangleIcon />
          <div>{t('entities:lowFaceSimilarityConfirmation.title')}</div>
        </div>
        <div className={st.Description}>
          {t('entities:lowFaceSimilarityConfirmation.description', { context })}
        </div>
      </div>
      <div className={st.Samples}>
        <FaceSample
          size="large"
          src={faceImageA}
        />
        <FaceSample
          size="large"
          src={faceImageB}
        />
        <Tag
          className={st.Tag}
          icon={<DetectionIcon />}
          value={convertSimilarity(similarity)}
        />
      </div>
      <div className={st.Buttons}>
        <Control.Button
          kind="attention"
          onClick={onConfirm}
        >
          {t('entities:lowFaceSimilarityConfirmation.confirm_button', { context })}
        </Control.Button>
        <Control.Button
          kind="secondary"
          onClick={onCancel}
          variant="outlined"
        >
          {t('entities:lowFaceSimilarityConfirmation.cancel_button')}
        </Control.Button>
      </div>
    </div>
  );
}

export { LowFaceSimilarityConfirmation };
